import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, OutlinedInput, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum, Minimum_Order_Value_Type_Enum } from '@kheops/graphql';
import { useLocation, useNavigate } from 'react-router';
import DiscountImage from '../../assets/images/3d-icons/discount.png';
import ClipboardImage from '../../assets/images/3d-icons/clipboard.png';
import PackageImage from '../../assets/images/3d-icons/package.png';
import { ContractConditions } from '../../chats/state';
import { currentContextAtom } from '../../state';
import CustomPriceListsSelect from './CustomPriceListsSelect';
import CommissionInfo from './CommissionInfo';
import CatalogsSelect from './CatalogsSelect';
import MinimumOrderValueField from './MinimumOrderValueField';

enum PriceModeEnum {
  DEFAULT = 'DEFAULT',
  CONTRACT_DISCOUNT = 'CONTRACT_DISCOUNT',
  CUSTOM_PRICE_LIST = 'CUSTOM_PRICE_LIST',
}

const PriceModeWordings = {
  [PriceModeEnum.DEFAULT]: {
    button: 'contracts:default_price',
    description: 'contracts:default_price_description',
  },
  [PriceModeEnum.CONTRACT_DISCOUNT]: {
    button: 'contracts:global_discount',
    description: 'contracts:global_discount_description',
  },
  [PriceModeEnum.CUSTOM_PRICE_LIST]: {
    button: 'contracts:custom_price_list',
    description: 'contracts:custom_price_list_description',
  },
};

export interface ContractUpdateDialogProps {
  open: boolean;
  onClose: (response?: ContractConditions) => void;
  title: string;
  description: string;
  submitLabel: string;
  contractId: string;
  firstOffer?: boolean;
  conditions: Partial<ContractConditions> & Pick<ContractConditions, 'supplyingCompanyId'>;
  commissionRate?: number;
  isNegociating?: boolean;
}

export interface ContractConditionsForm {
  discount: number;
  minimumOrderValue: number;
  minimumOrderValueType: Minimum_Order_Value_Type_Enum;
  subCatalogId?: string | null;
  subCatalogName?: string;
  customPriceListId?: string | null;
  customPriceListName?: string;
}

export default function ContractConditionsDialog({ open, onClose, title, description, submitLabel, contractId, firstOffer, conditions, commissionRate, isNegociating = firstOffer }: ContractUpdateDialogProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'catalogs', 'contracts', 'price']);
  const location = useLocation();
  const navigate = useNavigate();
  const { realm } = useAtomValue(currentContextAtom);
  const newSubCatalogId = location.state?.newSubCatalogId;
  const newSubCatalogName = location.state?.newSubCatalogName;
  const newCustomPriceListId = location.state?.newCustomPriceListId;
  const newCustomPriceListName = location.state?.newCustomPriceListName;
  const form = useForm<ContractConditionsForm>({
    defaultValues: {
      subCatalogId: newSubCatalogId || conditions?.sub_catalog_id,
      subCatalogName: newSubCatalogName || conditions?.catalogName,
      customPriceListId: newCustomPriceListId || conditions?.custom_price_list_id,
      customPriceListName: newCustomPriceListName || conditions?.customPriceListName,
      discount: new Decimal((conditions?.discount || 0)).times(100).toNumber(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      minimumOrderValue: conditions?.minimum_order_value || '' as any,
      minimumOrderValueType: conditions?.minimum_order_value_type || Minimum_Order_Value_Type_Enum.Price,
    },
    mode: 'onSubmit',
  });
  const discount = form.watch('discount');
  const [priceMode, setPriceMode] = useState<PriceModeEnum>(PriceModeEnum.DEFAULT);
  const isBuyerEditingDiscount = realm === Business_Profile_Enum.Buyer && !!conditions?.discount;
  /* A freshly created sub-catalog / custom price list is seeded into the form's
   defaultValues, so react-hook-form never flags it as dirty even though it is
   a real, pending change the user came back to apply. Treat its presence as a
   change so the submit button is enabled and the selection is actually saved.
   Captured once at mount because the parent clears location.state (via
   navigate('.', { replace: true })) right after opening the dialog, so reading
   it live would evaluate to false a render later. */
  const [hasNewlyCreatedSelection] = useState(() => !!newSubCatalogId || !!newCustomPriceListId);

  const handleContractConditionsSubmit = (data: ContractConditionsForm): void => {
    if (!form.formState.isDirty && !hasNewlyCreatedSelection && !firstOffer) {
      onClose();
    } else {
      onClose({
        supplyingCompanyId: conditions!.supplyingCompanyId!,
        discount: data.discount ? new Decimal(data.discount).dividedBy(100).toNumber() : 0,
        minimum_order_value: data.minimumOrderValue!,
        minimum_order_value_type: data.minimumOrderValueType!,
        custom_price_list_id: data.customPriceListId,
        customPriceListName: data.customPriceListName,
        proposer_business_profile: realm,
        sub_catalog_id: data.subCatalogId,
        catalogName: data.subCatalogName,
      });

      if (newSubCatalogId || newCustomPriceListId) {
        navigate('.', { replace: true });
      }
    }
  };

  const handleDiscountChange = (newDiscount: number): void => {
    const preciseNewDiscount = Decimal.min(new Decimal(newDiscount), 100).times(100).trunc().dividedBy(100);

    form.setValue('discount', preciseNewDiscount.toNumber(), { shouldDirty: true });
    form.setValue('customPriceListId', null);
    form.setValue('customPriceListName', undefined);
  };

  const handleCustomPriceListChange = (customPriceListId: string, customPriceListName: string): void => {
    form.setValue('discount', 0, { shouldDirty: true });
    form.setValue('customPriceListId', customPriceListId);
    form.setValue('customPriceListName', customPriceListName);
  };

  useEffect((): void => {
    if (conditions?.discount) {
      setPriceMode(PriceModeEnum.CONTRACT_DISCOUNT);
    } else if (conditions?.custom_price_list_id || newCustomPriceListId) {
      setPriceMode(PriceModeEnum.CUSTOM_PRICE_LIST);
    }
  }, [conditions, newCustomPriceListId]);

  useEffect((): void => {
    // Buyers have only access to discount if it's set and do not need to trigger any changes on the price mode
    if (realm === Business_Profile_Enum.Buyer) {
      return;
    }

    switch (priceMode) {
      case PriceModeEnum.DEFAULT:
        handleDiscountChange(0);
        break;
      case PriceModeEnum.CONTRACT_DISCOUNT:
        handleDiscountChange(new Decimal((conditions?.discount || 0)).times(100).toNumber());
        break;
      case PriceModeEnum.CUSTOM_PRICE_LIST:
        handleCustomPriceListChange(newCustomPriceListId || conditions?.custom_price_list_id || '', newCustomPriceListName || conditions?.customPriceListName || '');
        break;
    }
  }, [priceMode]);

  const discountInput = useMemo((): React.JSX.Element => (
    <Controller
      control={form.control}
      name="discount"
      rules={{
        min: 0,
        max: 100,
        required: isBuyerEditingDiscount,
      }}
      render={() => (
        <OutlinedInput
          type="number"
          size="small"
          placeholder={t('contracts:enter_your_discount')}
          value={!Number.isNaN(discount) ? discount : ''}
          endAdornment="%"
          onChange={(event) => handleDiscountChange(parseFloat(event.target.value))}
        />
      )}
    />
  ), [discount]);

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      sx={{
        '& .MuiPaper-root.MuiDialog-paper': {
          minWidth: {
            xs: 'calc(100vw - 64px)',
            sm: 640,
          },
        },
      }}
    >
      <DialogTitle>
        <Typography>{title}</Typography>
        <Typography>{description}</Typography>
      </DialogTitle>
      <Box
        component="form"
        onSubmit={form.handleSubmit(handleContractConditionsSubmit)}
      >
        <FormProvider {...form}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', py: 0 }}>
            {realm === Business_Profile_Enum.Supplier && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1.5, px: 0.5 }}>
                <Typography variant="titleLarge" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <img src={ClipboardImage} height={24} width={24} /> {t('catalogs:catalog')}
                </Typography>
                <Controller
                  control={form.control}
                  name="subCatalogId"
                  render={({ field }) => (
                    <CatalogsSelect
                      value={field.value || undefined}
                      onChange={(id, name) => {
                        field.onChange(id);
                        form.setValue('subCatalogName', name);
                      }}
                      contractId={contractId}
                      isNegociating={isNegociating}
                    />
                  )}
                />
              </Box>
            )}
            {(realm === Business_Profile_Enum.Supplier || isBuyerEditingDiscount) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1.5, px: 0.5 }}>
                <Typography variant="titleLarge" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <img src={DiscountImage} height={24} width={24} /> {t('common:prices')}
                </Typography>
                {realm === Business_Profile_Enum.Supplier && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      {Object.values(PriceModeEnum).map((value) => {
                        const selected = value === priceMode;

                        return (
                          <Chip
                            key={value}
                            className={selected ? 'Kheops-selected' : ''}
                            variant="filter"
                            onClick={() => setPriceMode(value)}
                            label={t(PriceModeWordings[value].button)}
                          />
                        );
                      })}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        backgroundColor: 'surfaceContainerLow.main',
                        boxSizing: 'border-box',
                        borderRadius: 3,
                        width: '100%',
                        p: 2,
                        alignSelf: 'start',
                      }}
                    >
                      <Typography variant="bodySmall">
                        {t(PriceModeWordings[priceMode].description)}
                      </Typography>
                      {priceMode === PriceModeEnum.CONTRACT_DISCOUNT && discountInput}
                      {priceMode === PriceModeEnum.CUSTOM_PRICE_LIST && (
                        <Controller
                          control={form.control}
                          name="customPriceListId"
                          render={({ field }) => (
                            <CustomPriceListsSelect
                              value={field.value || undefined}
                              onChange={handleCustomPriceListChange}
                              contractId={contractId}
                            />
                          )}
                        />

                      )}
                    </Box>
                  </>
                )}
                {isBuyerEditingDiscount && discountInput}
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1.5, px: 0.5 }}>
              <Typography variant="titleLarge" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <img src={PackageImage} height={24} width={24} /> {t('common:minimum_order_value')}
              </Typography>
              <MinimumOrderValueField
                minimumOrderValueName="minimumOrderValue"
                minimumOrderValueTypeName="minimumOrderValueType"
                hideType={realm === Business_Profile_Enum.Buyer && (firstOffer || !conditions?.minimum_order_value_type)}
              />
            </Box>
            {realm === Business_Profile_Enum.Supplier && (
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  py: 1.5,
                }}
              >
                <CommissionInfo commission={commissionRate as number} />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ py: 2 }}>
            <Button onClick={() => onClose()} variant="outlined">
              {t('common:cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={(!firstOffer && !form.formState.isDirty && !hasNewlyCreatedSelection) || !form.formState.isValid}
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </FormProvider>
      </Box>
    </Dialog>
  );
}
